import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  useTheme,
} from "@mui/material";
import {
  ArrowDownward as ArrowDownwardIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import { useStores } from "../../../use-stores";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { getModernPageColors } from "../../../../../styles/modernListingPageSx";

interface PartnerApiKeyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: number;
  customerName?: string;
}

interface GeneratedKeys {
  api_key: string;
  secret_key: string;
  api_user_email?: string;
}

const PartnerApiKeyDialog: React.FC<PartnerApiKeyDialogProps> = ({
  isOpen,
  onClose,
  customerId,
  customerName,
}) => {
  const { customerStore } = useStores();
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const c = theme.palette.customColors;

  const [apiKeyName, setApiKeyName] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedKeys, setGeneratedKeys] = useState<GeneratedKeys | null>(
    null,
  );

  useEffect(() => {
    if (!isOpen) {
      setApiKeyName("");
      setIsGenerating(false);
      setError(null);
      setGeneratedKeys(null);
    }
  }, [isOpen]);

  const handleGenerate = async () => {
    if (!customerId) return;
    setIsGenerating(true);
    setError(null);
    try {
      const res = await customerStore.generatePartnerApiKey(
        Number(customerId),
        apiKeyName.trim() || undefined,
      );

      if (res?.result?.api_key && res?.result?.shared_secret) {
        setGeneratedKeys({
          api_key: res.result.api_key,
          secret_key: res.result.shared_secret,
          api_user_email: res.result.api_user_email,
        });
      } else {
        setError(
          "Key was generated but the credentials were not returned. Disable it and generate a new one.",
        );
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          "Could not generate API key. Make sure customer is configured as a partner.",
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      setError("Could not copy to clipboard.");
    }
  };

  const handleDownloadJson = () => {
    if (!generatedKeys) return;
    const data = {
      api_key: generatedKeys.api_key,
      secret_key: generatedKeys.secret_key,
    };

    const dataStr =
      "data:text/json;charset=utf-8," +
      encodeURIComponent(JSON.stringify(data, null, 2));
    const downloadAnchor = document.createElement("a");
    downloadAnchor.setAttribute("href", dataStr);
    downloadAnchor.setAttribute(
      "download",
      `partner-credentials-customer-${customerId}.json`,
    );
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      height: 42,
      backgroundColor: colors.surface,
      fontSize: "0.875rem",
      color: colors.title,
      "& fieldset": {
        borderColor: colors.border,
      },
      "&:hover fieldset": {
        borderColor: colors.accentPurple,
      },
      "&.Mui-focused fieldset": {
        borderColor: colors.accentPurple,
        borderWidth: 1.5,
      },
    },
  };

  const credentialBoxSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: `1px solid ${c.softBorder}`,
    borderRadius: 2,
    px: 1.75,
    py: 1.25,
    backgroundColor: colors.surface,
    mb: 2.5,
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: { xs: 2.5, sm: 3.5 },
          backgroundColor: colors.surface,
          boxShadow: `0px 20px 25px -5px ${c.shadowMedium}, 0px 10px 10px -5px ${c.shadowVeryLight}`,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <CustomTypography variant="heading4" color={colors.title}>
          {generatedKeys ? "Your new key:" : "Generate Partner API Key"}
        </CustomTypography>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            border: `1px solid ${colors.border}`,
            borderRadius: 2,
            p: "6px",
            color: colors.subtitle,
            "&:hover": {
              backgroundColor: c.lightGrayBackground,
              borderColor: c.switchTrackGray,
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {error && (
        <Box
          sx={{
            mb: 2.5,
            p: 1.5,
            borderRadius: 2,
            backgroundColor: `${colors.error}14`,
            border: `1px solid ${c.borderErrorRedColor}55`,
            color: colors.error,
          }}
        >
          <CustomTypography variant="body2" color={colors.error} fontWeight={500}>
            {error}
          </CustomTypography>
        </Box>
      )}

      {generatedKeys ? (
        <Box>
          <CustomTypography
            variant="body1"
            color={colors.title}
            fontWeight={600}
            style={{ marginBottom: 6 }}
          >
            API key
          </CustomTypography>
          <Box sx={credentialBoxSx}>
            <CustomTypography
              variant="body1"
              color={c.navyText}
              style={{ wordBreak: "break-all", marginRight: 12 }}
            >
              {generatedKeys.api_key}
            </CustomTypography>
            <IconButton
              size="small"
              onClick={() => handleCopy(generatedKeys.api_key)}
              sx={{ color: c.mutedSlateText, flexShrink: 0, p: 0.5 }}
            >
              <ContentCopyIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Box>

          <CustomTypography
            variant="body1"
            color={colors.title}
            fontWeight={600}
            style={{ marginBottom: 6 }}
          >
            Secret key
          </CustomTypography>
          <Box sx={{ ...credentialBoxSx, mb: 2 }}>
            <CustomTypography
              variant="body1"
              color={c.navyText}
              style={{ wordBreak: "break-all", marginRight: 12 }}
            >
              {generatedKeys.secret_key}
            </CustomTypography>
            <IconButton
              size="small"
              onClick={() => handleCopy(generatedKeys.secret_key)}
              sx={{ color: c.mutedSlateText, flexShrink: 0, p: 0.5 }}
            >
              <ContentCopyIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Box>

          <CustomTypography
            variant="body1"
            color={c.mutedSlateText}
            style={{ marginBottom: 24 }}
          >
            Please copy it now and write it down somewhere safe. You will not be
            able to see it again.
          </CustomTypography>

          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <AppButton
              label="Download JSON"
              variant="grayscale"
              startIcon={<ArrowDownwardIcon />}
              onClick={handleDownloadJson}
            />
          </Box>
        </Box>
      ) : (
        <Box>
          <CustomTypography
            variant="body2"
            color={colors.subtitle}
            style={{ marginBottom: 20 }}
          >
            Generate a partner API key for customer{" "}
            <Box component="span" sx={{ fontWeight: 700, color: colors.title }}>
              #{customerId}
            </Box>
            {customerName ? ` (${customerName})` : ""}.
          </CustomTypography>

          <CustomTypography
            variant="heading8"
            color={c.navyText}
            style={{ marginBottom: 6 }}
          >
            API Key Name
          </CustomTypography>
          <TextField
            fullWidth
            size="small"
            placeholder="e.g. Production Key"
            value={apiKeyName}
            onChange={(e) => {
              setApiKeyName(e.target.value);
              if (error) setError(null);
            }}
            sx={inputStyles}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              mt: 3.5,
            }}
          >
            <AppButton
              label="Close"
              variant="grayscale"
              onClick={onClose}
              customStyles={{ minWidth: 96 }}
            />
            <AppButton
              label={
                isGenerating ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Generate"
                )
              }
              variant="primary"
              disabled={isGenerating}
              onClick={() => {
                void handleGenerate();
              }}
              customStyles={{ minWidth: 110 }}
            />
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

export default PartnerApiKeyDialog;
